import { useState, useMemo } from "react"
import { Link } from "react-router-dom"
import { SiteHeader } from "@/components/site-header"
import { usePermissions } from "@/hooks/use-permissions"
import { Input } from "@/components/ui/input"
import {
  ShieldIcon, UserIcon, BookOpenIcon, WrenchIcon, PlusSquareIcon,
  FileOutputIcon, DatabaseIcon, AlertCircleIcon, FileTextIcon,
  SmartphoneIcon, TableIcon, GitBranchIcon, KeyRoundIcon, TruckIcon,
  ScissorsIcon, SearchIcon, HandshakeIcon, GitMergeIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"

interface DocEntry {
  title:       string
  description: string
  href:        string
  icon:        React.ReactNode
  roles:       string[]
  category:    string
  tags?:       string[]
}

const ALL_DOCS: DocEntry[] = [
  // ── SmartGT / Exportaciones ────────────────────────────────────────────────
  {
    title:       "Mapeos para exportación",
    description: "Definición columna a columna de los CSV exportados al sistema SmartGT.",
    href:        "/docs/mapeos-exportacion",
    icon:        <FileOutputIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "SmartGT",
    tags:        ["csv", "exportar", "columnas", "smartgt"],
  },
  {
    title:       "Formato SmartGT v2 — Columnas",
    description: "Referencia de las 86 columnas del archivo ucarga v2: qué se guarda, en qué tabla y qué se ignora.",
    href:        "/docs/smartgt-v2-formato",
    icon:        <TableIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "SmartGT",
    tags:        ["ucarga", "v2", "formato", "columnas", "smartgt"],
  },
  {
    title:       "Reglas — Documentos de Carga",
    description: "Reglas completas de generación del CSV documentos-de-carga: exclusiones, parseo de CRT e invoice, y casos especiales.",
    href:        "/docs/reglas-documentos-carga",
    icon:        <FileTextIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "SmartGT",
    tags:        ["crt", "documentos", "carga", "csv", "invoice", "reglas"],
  },
  {
    title:       "Cálculo de Alertas en CRTs",
    description: "Lógica de validación de CRTs y cómo se calculan las alertas para cada campo.",
    href:        "/docs/calculo-alertas",
    icon:        <AlertCircleIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "SmartGT",
    tags:        ["alertas", "crt", "validacion", "campos"],
  },

  // ── Cargas y Viajes ────────────────────────────────────────────
  {
    title:       "Importación masiva de facturación",
    description: "Cómo funciona el import de facturación desde CSV: formato del archivo, flujo de preview, bulk overrides, arquitectura técnica y límites.",
    href:        "/docs/importacion-facturacion",
    icon:        <FileOutputIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "Cargas y Viajes",
    tags:        ["facturacion", "importar", "csv", "bulk", "tags", "observaciones", "billing"],
  },
  {
    title:       "Exportación de Cargas — Batea",
    description: "Mapeo columna a columna de los 3 exportadores CSV de Cargas (batea): datos de carga, documentos de carga y valorización. Diferencias con SmartGT.",
    href:        "/docs/exportacion-cargas",
    icon:        <FileOutputIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "Cargas y Viajes",
    tags:        ["exportar", "csv", "cargas", "batea", "datos", "documentos", "valorizacion", "columnas"],
  },
  {
    title:       "Exportación de Viajes y Cargas — Patrón",
    description: "Patrón estándar de checkboxes, paginación 25/50/100, barra de selección y preview CSV/XLS. Checklist para replicar en nuevas páginas.",
    href:        "/docs/exportacion-viajes",
    icon:        <FileOutputIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "Cargas y Viajes",
    tags:        ["exportar", "csv", "xls", "viajes", "cargas", "checkboxes", "paginacion", "preview", "patron"],
  },
  {
    title:       "Alertas de Envíos — shipment-alerts",
    description: "Lógica centralizada de niveles de alerta por estado: planificado demorado, activo sin llegada a origen, en_curso sin llegada a destino. Referencia de la función shipmentAlertLevel() y sus exports.",
    href:        "/docs/alertas-envios",
    icon:        <AlertCircleIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "Cargas y Viajes",
    tags:        ["alertas", "kanban", "planificado", "activo", "en_curso", "colores", "urgencia"],
  },
  {
    title:       "Estados de Envíos y Viajes",
    description: "Todos los estados de shipments y trips, quién los actualiza, cómo se sincronizan entre sí y su impacto en listas y exports.",
    href:        "/docs/estados-envios-viajes",
    icon:        <GitBranchIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "Cargas y Viajes",
    tags:        ["estados", "envios", "viajes", "sincronizacion", "flujo"],
  },
  {
    title:       "Cargas y Viajes — Asignación de Móvil",
    description: "Cómo funciona la asignación de móvil en cargas y viajes: unicidad, campos directos en trips, modificación y selects de recursos.",
    href:        "/docs/cargas-movil-viajes",
    icon:        <TruckIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "Cargas y Viajes",
    tags:        ["movil", "asignacion", "chofer", "camion", "semi", "unicidad"],
  },
  {
    title:       "Acciones sobre viajes",
    description: "Interconexión, Volver a base y Dividir viaje: cómo funcionan, cuándo se habilitan, qué datos se calculan automáticamente y cómo se configura la ciudad base.",
    href:        "/docs/acciones-viajes",
    icon:        <GitMergeIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "Cargas y Viajes",
    tags:        ["interconexión", "volver a base", "dividir", "split", "viaje", "chofer", "camion", "trailer", "chivilcoy", "retorno"],
  },
  {
    title:       "App Mobile — Flujo Batea",
    description: "Flujo secuencial de 5 hitos para viajes Batea en la app del chofer: hitos, permisos de driver, ventana de llegada y destino final.",
    href:        "/docs/batea-mobile",
    icon:        <SmartphoneIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "Cargas y Viajes",
    tags:        ["batea", "mobile", "hitos", "app", "chofer", "driver"],
  },
  {
    title:       "Envíos Multi-pierna — Batea",
    description: "Cómo funciona el flag is_multi_leg: activación desde la web, transición automática al registrar salida de origen, comportamiento en la app de cada chofer y archivos involucrados.",
    href:        "/docs/batea-multi-pierna",
    icon:        <GitBranchIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "Cargas y Viajes",
    tags:        ["batea", "multi-pierna", "multi-leg", "chofer", "transicion", "pierna", "is_multi_leg"],
  },

  // ── Relaciones ──────────────────────────────────────────────────────────────
  {
    title:       "Contratos de Transporte",
    description: "Modelo de datos, generación de semanas, override de objetivo mensual y cálculo de KPIs (TN real, % cumplimiento, camiones necesarios).",
    href:        "/docs/contratos",
    icon:        <HandshakeIcon className="size-5" />,
    roles:       ["admin", "operator"],
    category:    "Relaciones",
    tags:        ["contratos", "objetivo", "semanas", "tonelaje", "kpi", "batea"],
  },

  // ── Sistema ────────────────────────────────────────────────────────────────
  {
    title:       "Guía del Operador",
    description: "Acceso al sistema, sesiones, intentos fallidos y cierre de sesión.",
    href:        "/docs/operator",
    icon:        <UserIcon className="size-5" />,
    roles:       ["admin", "operator", "fleet_manager", "driver"],
    category:    "Sistema",
    tags:        ["operador", "sesion", "acceso", "login"],
  },
  {
    title:       "Guía de Administración",
    description: "Roles, sesiones, rate limiting, creación de usuarios y variables de entorno.",
    href:        "/docs/admin",
    icon:        <ShieldIcon className="size-5" />,
    roles:       ["admin"],
    category:    "Sistema",
    tags:        ["admin", "roles", "usuarios", "entorno", "rate limit"],
  },
  {
    title:       "Roles y Permisos — Guía técnica",
    description: "Cómo funciona el sistema RBAC: mapa de permisos, hook usePermissions(), guards de ruta y configuración de superadmin.",
    href:        "/docs/roles-permisos",
    icon:        <KeyRoundIcon className="size-5" />,
    roles:       ["admin"],
    category:    "Sistema",
    tags:        ["rbac", "permisos", "roles", "superadmin", "guards"],
  },
  {
    title:       "Seguridad — Pendientes",
    description: "Ítems de seguridad identificados antes del deploy a producción. Incluye estado resuelto/pendiente.",
    href:        "/docs/seguridad-pendientes",
    icon:        <ShieldIcon className="size-5" />,
    roles:       ["admin"],
    category:    "Sistema",
    tags:        ["seguridad", "deploy", "produccion", "pendientes"],
  },

  // ── Desarrollo ─────────────────────────────────────────────────────────────
  {
    title:       "Guía CRUD de Entidades",
    description: "Paso a paso para crear una nueva entidad de punta a punta.",
    href:        "/docs/crud-entidades",
    icon:        <WrenchIcon className="size-5" />,
    roles:       ["admin"],
    category:    "Desarrollo",
    tags:        ["crud", "entidad", "crear", "api", "ui"],
  },
  {
    title:       "Agregar campos a un modelo existente",
    description: "Cómo agregar columnas simples y FKs: schema, migración, Zod, API y UI.",
    href:        "/docs/agregar-campos",
    icon:        <PlusSquareIcon className="size-5" />,
    roles:       ["admin"],
    category:    "Desarrollo",
    tags:        ["campos", "schema", "migracion", "zod", "fk"],
  },
  {
    title:       "Gestión de DB + Drizzle",
    description: "Problemas frecuentes con migraciones: bootstrapping, secuencias y hashes manuales.",
    href:        "/docs/db-drizzle",
    icon:        <DatabaseIcon className="size-5" />,
    roles:       ["admin"],
    category:    "Desarrollo",
    tags:        ["drizzle", "migraciones", "db", "postgres", "bootstrap"],
  },
]

const CATEGORY_ORDER = ["Cargas y Viajes", "SmartGT", "Relaciones", "Sistema", "Desarrollo"]

export default function DocsPage() {
  const { role } = usePermissions()
  const [search, setSearch] = useState("")

  const visible = useMemo(() => {
    const byRole = ALL_DOCS.filter(d => role && d.roles.includes(role))
    if (!search.trim()) return byRole
    const term = search.toLowerCase()
    return byRole.filter(d =>
      d.title.toLowerCase().includes(term) ||
      d.description.toLowerCase().includes(term) ||
      d.category.toLowerCase().includes(term) ||
      (d.tags ?? []).some(t => t.includes(term))
    )
  }, [role, search])

  const grouped = useMemo(() => {
    const map = new Map<string, DocEntry[]>()
    for (const doc of visible) {
      const list = map.get(doc.category) ?? []
      list.push(doc)
      map.set(doc.category, list)
    }
    // Sort categories in defined order
    return CATEGORY_ORDER
      .filter(cat => map.has(cat))
      .map(cat => ({ category: cat, docs: map.get(cat)! }))
  }, [visible])

  return (
    <>
      <SiteHeader title="Documentación" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">

        {/* Header row */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <p className="text-sm text-muted-foreground shrink-0">
            Guías disponibles para tu rol:{" "}
            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{role}</span>
          </p>
          <div className="relative w-full max-w-xs">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Buscar documentación..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        {/* Empty state */}
        {grouped.length === 0 && (
          <div className="flex items-center gap-3 rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
            <BookOpenIcon className="size-5 shrink-0" />
            {search ? `Sin resultados para "${search}".` : "No hay documentación disponible para tu rol."}
          </div>
        )}

        {/* Categories */}
        <div className="space-y-8">
          {grouped.map(({ category, docs }) => (
            <section key={category}>
              <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                {category}
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {docs.map((doc) => (
                  <Link key={doc.href} to={doc.href} className="group">
                    <div className={cn(
                      "h-full rounded-lg border bg-card p-4 flex flex-col gap-2",
                      "transition-colors hover:bg-accent/50 hover:border-accent cursor-pointer",
                    )}>
                      <div className="flex items-center gap-2.5">
                        <span className="text-muted-foreground group-hover:text-foreground transition-colors shrink-0">
                          {doc.icon}
                        </span>
                        <p className="text-sm font-semibold leading-tight">{doc.title}</p>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {doc.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>

      </div>
    </>
  )
}
